export interface DetailedAddress {
  formattedAddressKr: string;
  formattedAddressEn: string;
  roadName?: string;
  district?: string;
  city?: string;
  cityEn?: string;
  postcode?: string;
}

/**
 * Reverse geocoding via OpenStreetMap Nominatim.
 * First requests Korean locale, then falls back to English.
 * Builds proper Korean 도로명 주소 format.
 */
export async function reverseGeocodeCoords(lat: number, lng: number): Promise<DetailedAddress> {
  try {
    // Request Korean first
    const urlKr = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=ko`;

    const resKr = await fetch(urlKr, {
      headers: {
        'User-Agent': 'KSpotFinder/2.0 (korea-spotfinder.pages.dev)',
      },
    });

    if (!resKr.ok) throw new Error(`Nominatim error: ${resKr.status}`);

    const dataKr = await resKr.json();
    const addr = dataKr.address || {};

    // Build Korean address in proper order: 시/도 → 구/군 → 도로 → 번지
    const province = addr.province || addr.state || '';
    const city = addr.city || addr.town || addr.county || province;
    const district = addr.city_district || addr.suburb || addr.borough || addr.quarter || addr.district || '';
    const road = addr.road || addr.pedestrian || addr.street || '';
    const houseNumber = addr.house_number || '';
    const neighbourhood = addr.neighbourhood || '';
    const postcode = addr.postcode || '';

    // Korean format: 서울특별시 종로구 사직로 161
    let formattedKr = '';
    if (road) {
      formattedKr = [city, district, road, houseNumber].filter(Boolean).join(' ');
    } else if (neighbourhood) {
      formattedKr = [city, district, neighbourhood].filter(Boolean).join(' ');
    } else {
      formattedKr = dataKr.display_name || [city, district].filter(Boolean).join(' ');
    }

    // Also get English name for display
    let formattedEn = '';
    let cityEn = '';
    try {
      const urlEn = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=en`;
      const resEn = await fetch(urlEn, {
        headers: { 'User-Agent': 'KSpotFinder/2.0 (korea-spotfinder.pages.dev)' },
      });
      if (resEn.ok) {
        const dataEn = await resEn.json();
        const addrEn = dataEn.address || {};
        const provEn = addrEn.province || addrEn.state || '';
        cityEn = addrEn.city || addrEn.town || addrEn.county || provEn;
        const distEn = addrEn.city_district || addrEn.suburb || addrEn.borough || addrEn.quarter || addrEn.district || '';
        const roadEn = addrEn.road || addrEn.pedestrian || addrEn.street || '';
        formattedEn = [roadEn, distEn, cityEn].filter(Boolean).join(', ');
      }
    } catch {
      // Ignore English fallback error
    }

    return {
      formattedAddressKr: formattedKr || '대한민국',
      formattedAddressEn: formattedEn || 'South Korea',
      roadName: road,
      district,
      city,
      cityEn,
      postcode,
    };
  } catch (error) {
    console.warn('Reverse geocoding failed:', error);
    return {
      formattedAddressKr: '대한민국',
      formattedAddressEn: 'South Korea',
    };
  }
}
